import React, { useState } from 'react';
import { EmojiReaction, AVAILABLE_EMOJIS } from '../models/Chat';

interface EmojiReactionViewProps {
  reactions: Record<string, number>;
  onEmojiClick: (emoji: string) => void;
  userReactions: string[];
  canReact: boolean;
}

const EmojiReactionView: React.FC<EmojiReactionViewProps> = ({
  reactions,
  onEmojiClick,
  userReactions,
  canReact
}) => {
  const [floatingEmojis, setFloatingEmojis] = useState<EmojiReaction[]>([]);

  const addFloatingEmoji = (emoji: string) => {
    const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
    const newEmoji: EmojiReaction = {
      id,
      emoji,
      scale: 1,
      opacity: 1,
      position: { x: Math.random() * 200 - 100, y: -50 }
    };

    setFloatingEmojis(prev => [...prev, newEmoji]);
    animateEmoji(id);
  };

  const animateEmoji = (id: string) => {
    const interval = setInterval(() => {
      setFloatingEmojis(prev => {
        return prev.map(emoji => {
          if (emoji.id !== id) return emoji;

          const newScale = Math.max(0.3, emoji.scale - 0.02);
          const newOpacity = Math.max(0, emoji.opacity - 0.02);
          const newY = emoji.position.y - 3;

          if (newOpacity <= 0) {
            clearInterval(interval);
            return null;
          }

          return {
            ...emoji,
            scale: newScale,
            opacity: newOpacity,
            position: { ...emoji.position, y: newY }
          };
        }).filter(Boolean) as EmojiReaction[];
      });
    }, 50);
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {/* Floating emojis overlay — mirrors iOS EmojiReactionViewModel animation */}
      {floatingEmojis.map(emoji => (
        <div
          key={emoji.id}
          style={{
            position: 'absolute',
            fontSize: `${30 * emoji.scale}px`,
            opacity: emoji.opacity,
            left: `calc(50% + ${emoji.position.x}px)`,
            top: `${emoji.position.y}px`,
            pointerEvents: 'none',
            zIndex: 10
          }}
        >
          {emoji.emoji}
        </div>
      ))}

      {/* Fixed emoji row — same set and layout as iOS availableEmojis */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        padding: '6px 4px',
        backgroundColor: '#f2f2f7'
      }}>
        {AVAILABLE_EMOJIS.map(emoji => {
          const count = reactions[emoji] || 0;
          const reacted = userReactions.includes(emoji);
          return (
            <button
              key={emoji}
              onClick={() => {
                onEmojiClick(emoji);
                if (canReact) addFloatingEmoji(emoji);
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                background: reacted ? '#e5e5ea' : 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 8px',
                borderRadius: '8px',
                fontSize: '24px'
              }}
            >
              <span>{emoji}</span>
              <span style={{
                fontSize: '10px',
                color: '#666',
                opacity: count > 0 ? 1 : 0.5
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default EmojiReactionView;
